import { createSelector } from "@reduxjs/toolkit";

import { FormId } from "../../../components/enums";
import { UIState } from "../../../store/HOC/genericUI.slice";
import { RootState } from "../../../store/rootReducer";
import {
  generateAddress,
  generatePhone,
  mapRoleTypeToLabel,
} from "../../../utilities/forms";

import { ComponentId as AdditionalComponentId } from "./AdditionalData/enums";
import { ComponentId as ApplicantComponentId } from "./ApplicantData/enums";
import { ComponentId as DriverComponentId } from "./DriverData/enums";
import {
  ApplicantFormInputs,
  ApplicantAdditionalFormInputs,
  ApplicantDriverFormInputs,
  initialState,
} from "./applicant.slice";

export const selectShouldNotValidateByPesel = (store: RootState): boolean =>
  !!store.ac.applicant.data[FormId.APPLICANT_DRIVER][
    DriverComponentId.APPLICANT_DRIVER_LACK_OF_PESEL
  ] !==
  !!initialState.data[FormId.APPLICANT_DRIVER][
    DriverComponentId.APPLICANT_DRIVER_LACK_OF_PESEL
  ];

export const selectShouldNotValidateByDrivingLicense = (
  store: RootState
): boolean =>
  store.ac.applicant.data[FormId.APPLICANT_DRIVER][
    DriverComponentId.APPLICANT_DRIVER_LACK_OF_PESEL
  ] ===
  initialState.data[FormId.APPLICANT_DRIVER][
    DriverComponentId.APPLICANT_DRIVER_LACK_OF_PESEL
  ];

export const selectShouldDisplayOtherPersons = (store: RootState): boolean =>
  store.ac.applicant.data[FormId.APPLICANT_ADDITIONAL][
    AdditionalComponentId.APPLICANT_ADDITIONAL_ARE_OTHER_PEOPLE_INVOLVED
  ] !==
  initialState.data[FormId.APPLICANT_ADDITIONAL][
    AdditionalComponentId.APPLICANT_ADDITIONAL_ARE_OTHER_PEOPLE_INVOLVED
  ];

export const selectApplicantData = (store: RootState): ApplicantFormInputs =>
  store.ac.applicant.data[FormId.APPLICANT];

export const selectApplicantDriverData = (
  store: RootState
): ApplicantDriverFormInputs =>
  store.ac.applicant.data[FormId.APPLICANT_DRIVER];

export const selectApplicantAdditionalData = (
  store: RootState
): ApplicantAdditionalFormInputs =>
  store.ac.applicant.data[FormId.APPLICANT_ADDITIONAL];

export const selectApplicantAdditionalPersonsData = (
  store: RootState
): Partial<ApplicantAdditionalFormInputs> => ({
  [AdditionalComponentId.APPLICANT_ADDITIONAL_PERSONS]:
    store.ac.applicant.data[FormId.APPLICANT_ADDITIONAL][
      AdditionalComponentId.APPLICANT_ADDITIONAL_PERSONS
    ],
});

const selectApplicantNameSurname = createSelector(
  selectApplicantData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [
      data[ApplicantComponentId.APPLICANT_NAME],
      data[ApplicantComponentId.APPLICANT_SURNAME],
    ]
      .join(" ")
      .trim(),
  })
);

const selectApplicantTel = createSelector(selectApplicantData, (data) => ({
  name: "Tel.",
  value: generatePhone(data[ApplicantComponentId.APPLICANT_PHONE]),
}));

const selectApplicantEmail = createSelector(selectApplicantData, (data) => ({
  name: "Email",
  value: data[ApplicantComponentId.APPLICANT_EMAIL],
}));

const selectApplicantDriver = createSelector(selectApplicantData, (data) => ({
  name: "Czy osoba zgłaszająca kierowała pojazdem",
  value:
    data[ApplicantComponentId.APPLICANT_IS_DRIVER] === "true" ? "tak" : "nie",
}));

const selectApplicantSummaryData = createSelector(
  selectApplicantNameSurname,
  selectApplicantTel,
  selectApplicantEmail,
  selectApplicantDriver,
  (nameSurname, tel, email, driver) => [nameSurname, tel, email, driver]
);

const selectApplicantDriverNameSurname = createSelector(
  selectApplicantDriverData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [
      data[DriverComponentId.APPLICANT_DRIVER_NAME],
      data[DriverComponentId.APPLICANT_DRIVER_SURNAME],
    ]
      .join(" ")
      .trim(),
  })
);

const selectApplicantDriverTel = createSelector(
  selectApplicantDriverData,
  (data) => ({
    name: "Tel.",
    value: generatePhone(data[DriverComponentId.APPLICANT_DRIVER_PHONE]),
  })
);

const selectApplicantDriverEmail = createSelector(
  selectApplicantDriverData,
  (data) => ({
    name: "Email",
    value: data[DriverComponentId.APPLICANT_DRIVER_EMAIL],
  })
);
const selectApplicantDriverId = createSelector(
  selectShouldNotValidateByDrivingLicense,
  selectApplicantDriverData,
  (validateByPesel, data) => ({
    name: validateByPesel ? "PESEL" : "Nr. prawa jazdy",
    value: validateByPesel
      ? data[DriverComponentId.APPLICANT_DRIVER_PESEL]
      : data[DriverComponentId.APPLICANT_DRIVER_DRIVING_LICENSE],
  })
);

const selectApplicantDriverAddress = createSelector(
  selectApplicantDriverData,
  (data) => ({
    name: "Ulica, kod pocztowy, miejscowość",
    value: generateAddress(data),
  })
);

const selectApplicantDriverSummaryData = createSelector(
  selectApplicantDriverNameSurname,
  selectApplicantDriverTel,
  selectApplicantDriverEmail,
  selectApplicantDriverId,
  selectApplicantDriverAddress,
  (nameSurname, tel, email, id, address) => [
    nameSurname,
    tel,
    email,
    id,
    address,
  ]
);

const selectApplicantAdditional = createSelector(
  selectShouldDisplayOtherPersons,
  selectApplicantAdditionalPersonsData,
  (shouldDisplay, data) => {
    if (!shouldDisplay || data.persons === undefined) {
      return [];
    }

    return Object.values(data.persons).map((person, index) => ({
      name: `Osoba ${++index}`,
      value: [
        [person.name, person.surname].join(" ").trim(),
        ["rola:", mapRoleTypeToLabel[person.role]].join(" "),
        generatePhone(person.phone),
        person.email,
        generateAddress(person),
      ]
        .filter((item) => item.trim() !== "")
        .join("\r\n"),
    }));
  }
);

export const selectSummaryData = createSelector(
  selectApplicantSummaryData,
  selectApplicantDriverSummaryData,
  selectApplicantAdditional,
  (applicant, driver, otherPersons) => ({
    applicant,
    driver,
    otherPersons,
  })
);

export const selectFormData = createSelector(
  selectApplicantData,
  selectApplicantDriverData,
  selectApplicantAdditionalData,
  (applicant, driver, additional) => ({
    applicant,
    driver,
    additional: {
      [AdditionalComponentId.APPLICANT_ADDITIONAL_PERSONS]: Object.values(
        additional.persons
      ),
      [AdditionalComponentId.APPLICANT_ADDITIONAL_ARE_OTHER_PEOPLE_INVOLVED]:
        additional[
          AdditionalComponentId.APPLICANT_ADDITIONAL_ARE_OTHER_PEOPLE_INVOLVED
        ],
    },
  })
);

export const selectUIData = (store: RootState): UIState =>
  store.ac.applicant.ui;

export const selectApplicantAvailableCities = (store: RootState) =>
  store.ac.applicant.ui.applicantAvailableCities || [];
